import { isStrayCypherExpr } from './stray'

/**
 * Detail bundle for a single Compute host (`/plant/compute/hosts/:id`).
 * Returns the host node plus every TagProvider attached via
 * `HAS_TAG_PROVIDER` and every Database attached either via direct
 * `HAS_DATABASE` or — for Ignition gateways — via
 * `NETWORKS_WITH` → `db_server` → `HAS_DATABASE` (each with its
 * `CONTAINS_TABLE`-reachable Table count). The `UNWIND` null-sentinel
 * pattern keeps the row around when a host has zero databases —
 * sentinels are filtered in the extractors.
 *
 * Ingest hangs parseable JDBC catalogs on a synthetic `db_server`
 * Compute and only links the gateway with `NETWORKS_WITH` (see
 * `mapDatabases` / `PLANT_QUERY_DETAIL_CYPHER`). Plant View therefore
 * walks that hop so gateway host pages still list those catalogs —
 * the WebDev query workbench needs the Ignition host context.
 *
 * Tabular-dump ingest MERGEs a standalone `:Database` (`urn:db:<name>`)
 * with no Compute. When `$computeId` matches that Database and no
 * Compute row exists, the query returns the Database as
 * `standaloneDatabase` so the mapper can project a `db_server` host
 * and a one-item catalog list.
 *
 * Per-provider `tagCount` is the count of direct
 * `(:TagProvider)-[:PROVIDES]->(:Tag)` rows only. UDT-member leaves
 * are intentionally not counted here — `getPlantProviderTags` already
 * does the depth-unrolled count when the user opens a provider, and
 * doing it eagerly here (across every provider on the host) is
 * prohibitively expensive on real Ignition gateways (no
 * `:UDTDefinition(name)` index, no transaction timeout, and a
 * 5-deep traversal per provider can hang the page indefinitely).
 * The badge on the detail-page card therefore shows the direct
 * `:Tag` count; the in-browser total includes UDT-member leaves.
 *
 * Params: `{ computeId: string }`.
 */
export const PLANT_COMPUTE_DETAIL_CYPHER = `
            OPTIONAL MATCH (c:Compute {id: $computeId})
            OPTIONAL MATCH (d:Database {id: $computeId})
            WHERE c IS NULL AND NOT (d)<-[:HAS_DATABASE]-(:Compute)
            WITH c, d
            WHERE c IS NOT NULL OR d IS NOT NULL
            OPTIONAL MATCH (c)-[:HAS_TAG_PROVIDER]->(tp:TagProvider)
            CALL {
              WITH tp
              OPTIONAL MATCH (tp)-[:PROVIDES]->(t:Tag)
              RETURN count(distinct t) AS tagCount
            }
            WITH c, d, collect(distinct { provider: tp, tagCount: tagCount }) AS tagProviderSummaries
            OPTIONAL MATCH (c)-[:HAS_DATABASE]->(dbDirect:Database)
            WITH c, d, tagProviderSummaries, collect(distinct dbDirect) AS directDbs
            OPTIONAL MATCH (c)-[:NETWORKS_WITH]->(:Compute)-[:HAS_DATABASE]->(dbRemote:Database)
            WITH c, d, tagProviderSummaries, directDbs, collect(distinct dbRemote) AS remoteDbs
            WITH c, d, tagProviderSummaries,
                 CASE
                   WHEN d IS NOT NULL THEN [d]
                   ELSE [x IN (directDbs + remoteDbs) WHERE x IS NOT NULL]
                 END AS databases
            UNWIND (CASE WHEN size(databases) = 0 THEN [null] ELSE databases END) AS dbNode
            OPTIONAL MATCH (dbNode)-[:CONTAINS_TABLE]->(tbl:Table)
            WITH c, d, tagProviderSummaries, dbNode, count(distinct tbl) AS tableCount
            WITH c, d, tagProviderSummaries, collect(distinct {
                   database: dbNode,
                   tableCount: tableCount
                 }) AS databaseSummaries
            OPTIONAL MATCH (c)-[:MAPS_TO_CONTROLLER]->(ctrl:Controller)
            WITH c, d, tagProviderSummaries, databaseSummaries, collect(distinct ctrl) AS controllers
            OPTIONAL MATCH (c)-[:MAPS_TO_CONTROLLER]->(dcs:DcsController)
            WITH c, d, tagProviderSummaries, databaseSummaries, controllers,
                 collect(distinct dcs) AS dcsControllers
            OPTIONAL MATCH (c)<-[:MONITORED_BY]-(asset)
              WHERE (asset:Equipment OR asset:Line)
            WITH c, d, tagProviderSummaries, databaseSummaries, controllers, dcsControllers,
                 collect(DISTINCT CASE WHEN asset IS NULL THEN NULL ELSE {
                   id: asset.id,
                   name: asset.name,
                   kind: CASE WHEN asset:Line THEN 'line' ELSE 'equipment' END,
                   type: coalesce(asset.equipment_type, asset.line_code)
                 } END) AS monitoredAssetsRaw
            OPTIONAL MATCH (gw:Compute)-[:NETWORKS_WITH]->(c)
            WITH c, d, tagProviderSummaries, databaseSummaries, controllers, dcsControllers,
                 monitoredAssetsRaw, collect(distinct gw) AS gateways
            RETURN c,
                   d AS standaloneDatabase,
                   tagProviderSummaries,
                   databaseSummaries,
                   controllers,
                   dcsControllers,
                   gateways,
                   [a IN monitoredAssetsRaw WHERE a IS NOT NULL] AS monitoredAssets,
                   CASE WHEN c IS NULL THEN false ELSE ${isStrayCypherExpr('c', 'Compute')} END AS isStray
            LIMIT 10000
          `
